import { useState, useEffect, useRef, useCallback } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Maximize2, Minimize2, ExternalLink } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

interface Kuula3DViewerProps {
  kuulaUrl: string;
  unitNumber?: string;
  className?: string;
}

/**
 * Kuula.co 3D Tour Viewer Component
 * Supports Kuula.co embed format for 3D virtual tours
 */
export const Kuula3DViewer = ({ kuulaUrl, unitNumber = 'Unit', className = '' }: Kuula3DViewerProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Normalize Kuula URL to embed format
  const getEmbedUrl = (url: string): string => {
    // If already an embed URL, return as is
    if (url.includes('/embed/')) {
      return url;
    }
    
    // Extract share ID from various Kuula URL formats
    // Formats: https://kuula.co/share/XXXXX, https://kuula.co/post/XXXXX, etc.
    const shareIdMatch = url.match(/kuula\.co\/(?:share|post)\/([^/?]+)/);
    if (shareIdMatch) {
      return `https://kuula.co/share/${shareIdMatch[1]}?embed=1`;
    }
    
    // If it's already a valid URL, try to use it
    if (url.startsWith('http')) {
      return url.includes('embed') ? url : `${url}${url.includes('?') ? '&' : '?'}embed=1`;
    }
    
    // If it's just an ID, construct the embed URL
    return `https://kuula.co/share/${url}?embed=1`;
  };

  const embedUrl = getEmbedUrl(kuulaUrl);

  const toggleFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        // Enter fullscreen
        const element = containerRef.current;
        if (element) {
          await element.requestFullscreen();
          setIsFullscreen(true);
          trackEvent('property_3d_tour_fullscreen', { 
            unit: unitNumber,
            platform: 'kuula',
            action: 'enter' 
          });
        }
      } else {
        // Exit fullscreen
        await document.exitFullscreen();
        setIsFullscreen(false);
        trackEvent('property_3d_tour_fullscreen', { 
          unit: unitNumber,
          platform: 'kuula',
          action: 'exit' 
        });
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
      // Fallback to component-level fullscreen
      setIsFullscreen(!isFullscreen);
    }
  }, [unitNumber, isFullscreen]);

  useEffect(() => {
    // Track 3D tour view
    trackEvent('residences_3d_tour_viewed', { 
      unit: unitNumber,
      platform: 'kuula'
    });

    // Listen for fullscreen changes
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    // Listen for keyboard events
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F11') {
        e.preventDefault();
        toggleFullscreen();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [unitNumber, toggleFullscreen]);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const openInNewTab = () => {
    // Open original Kuula URL or embed URL
    const originalUrl = kuulaUrl.includes('kuula.co') ? kuulaUrl : embedUrl.replace('?embed=1', '');
    window.open(originalUrl, '_blank');
    trackEvent('property_3d_tour_external', { 
      unit: unitNumber,
      platform: 'kuula'
    });
  };

  if (hasError) {
    return (
      <div className={`flex flex-col items-center justify-center p-8 bg-muted/50 rounded-lg ${className}`}>
        <div className="text-center">
          <h3 className="font-display text-lg font-semibold text-foreground mb-2">
            3D Tour Unavailable
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            The virtual tour is temporarily unavailable. Please try again later or contact us for a personal tour.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={openInNewTab}
            className="gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Try External Link
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative bg-black rounded-lg overflow-hidden ${className}`}>
      {/* Loading State */}
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted">
          <div className="text-center">
            <Skeleton className="w-16 h-16 rounded-full mx-auto mb-4" />
            <Skeleton className="h-4 w-32 mx-auto mb-2" />
            <Skeleton className="h-3 w-24 mx-auto" />
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="absolute top-4 right-4 z-20 flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={toggleFullscreen}
          className="bg-white/90 hover:bg-white text-foreground"
        >
          {isFullscreen ? (
            <Minimize2 className="w-4 h-4" />
          ) : (
            <Maximize2 className="w-4 h-4" />
          )}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={openInNewTab}
          className="bg-white/90 hover:bg-white text-foreground"
        >
          <ExternalLink className="w-4 h-4" />
        </Button>
      </div>

      {/* Kuula Iframe */}
      <iframe
        src={embedUrl}
        title={`3D Virtual Tour - ${unitNumber}`}
        className={`w-full transition-all duration-300 ${
          isFullscreen ? 'h-screen' : 'h-[500px] lg:h-[600px]'
        }`}
        allow="fullscreen; xr-spatial-tracking; accelerometer; gyroscope; magnetometer"
        allowFullScreen
        onLoad={handleLoad}
        onError={handleError}
        style={{
          border: 'none',
          borderRadius: isFullscreen ? '0' : '0.5rem'
        }}
      />

      {/* Instructions Overlay */}
      {!isLoading && !hasError && (
        <div className="absolute bottom-4 left-4 right-4 z-20">
          <div className="bg-black/70 backdrop-blur-sm rounded-lg p-3 text-white text-sm">
            <p className="text-center">
              Use mouse to navigate • Scroll to zoom • Click and drag to look around • Press F11 for fullscreen
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

